import React, { useState, useEffect, useRef } from 'react'
import {
  Modal,
  Button,
  Slider,
  Tooltip,
  Icon,
  Input,
  Spin,
  message,
  Badge
} from 'antd'
import {
  editImageDeepAI,
  generateImageWithReferenceNanoBanana,
  getTaskStatus
} from '../../../../../services/api/aiVideoGeneration'
import { ImagePreview } from '../../../../../components'

const { TextArea } = Input

const ImageWithPlaceholder = ({ src, alt, aspectRatio, style }) => {
  const [loaded, setLoaded] = useState(false)
  const [w, h] = aspectRatio?.split(':').map(Number) || [1, 1]

  return (
    <div
      style={{
        ...style,
        position: 'relative',
        backgroundColor: '#f0f0f0',
        aspectRatio: `${w}/${h}`,
        overflow: 'hidden'
      }}
    >
      {/* Hidden listener for load completion */}
      <img
        src={src}
        alt=""
        style={{ display: 'none' }}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />

      {!loaded && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
            fontSize: '12px',
            zIndex: 1
          }}
        >
          {/* {aspectRatio} */}
        </div>
      )}

      <div
        style={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          width: '100%'
        }}
      >
        <ImagePreview
          src={src}
          alt={alt}
          style={{
            ...style,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
        />
      </div>
    </div>
  )
}

const COLORS = {
  primary: '#5057d5',
  primaryLight: '#e8f0fe',
  surface: '#ffffff',
  background: '#f8f9fa',
  text: '#202124',
  textSecondary: '#5f6368',
  textMuted: '#80868b',
  border: '#dadce0',
  borderLight: '#e8eaed',
  success: '#1e8e3e',
  error: '#d93025',
  white: '#ffffff'
}

const ImageEditorModal = ({
  visible,
  mode = 'kontext',
  onCancel,
  onConfirm,
  initialPrompt = '',
  referenceImages = [],
  aspectRatio,
  currentImage = null,
  videoId
}) => {
  const [prompt, setPrompt] = useState('')
  const [currentEditingImage, setCurrentEditingImage] = useState(currentImage)
  const [selectedReferences, setSelectedReferences] = useState([])
  const [isGenerating, setIsGenerating] = useState(false)
  const [conversation, setConversation] = useState([])
  const [selectedForSave, setSelectedForSave] = useState(null)
  const [showReferences, setShowReferences] = useState(true)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [showConfirmSave, setShowConfirmSave] = useState(false)
  const [inferenceSteps, setInferenceSteps] = useState(28)
  const [guidanceScale, setGuidanceScale] = useState(0.85)
  const chatEndRef = useRef(null)

  const scrollToBottom = () => {
    chatEndRef.current &&
      chatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [conversation])

  useEffect(() => {
    if (visible) {
      setPrompt('')
      setConversation([])
      setSelectedForSave(null)
      setShowReferences(true)
      setCurrentEditingImage(currentImage)
      setSelectedReferences([])
    }
  }, [visible, initialPrompt, mode, currentImage])

  const pollTask = async taskId => {
    const POLL_INTERVAL = 5000
    const MAX_ATTEMPTS = 60
    for (let i = 0; i < MAX_ATTEMPTS; i++) {
      try {
        const res = await getTaskStatus(taskId)
        const status = res?.data?.status
        if (status === 'success') return res?.data?.output
        if (status === 'failed' || status === 'error') {
          throw new Error(res?.data?.error || 'Task failed')
        }
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
      } catch (err) {
        if (err.message !== 'Task failed') console.error('Polling error', err)
        throw err
      }
    }
    throw new Error('Timeout')
  }

  const handleSend = async () => {
    if (!prompt.trim()) {
      message.error('Please enter a prompt')
      return
    }
    const usedReferences = [
      currentEditingImage,
      ...(selectedReferences || [])
    ].filter(Boolean)

    setConversation(prev => [
      ...prev,
      {
        type: 'user',
        content: prompt,
        timestamp: Date.now(),
        references: usedReferences
      }
    ])

    const loadingId = Date.now()
    setConversation(prev => [...prev, { type: 'loading', id: loadingId }])
    setIsGenerating(true)
    setPrompt('')

    try {
      let response
      if (mode === 'kontext') {
        response = await editImageDeepAI({
          prompt,
          image_to_be_edited: currentEditingImage,
          references: selectedReferences || [],
          num_inference_steps: inferenceSteps,
          guidance_scale: guidanceScale,
          ratio: aspectRatio,
          video_id: videoId
        })
      } else {
        response = await generateImageWithReferenceNanoBanana({
          prompt,
          ratio: aspectRatio,
          image_to_be_edited: currentEditingImage,
          reference_images: selectedReferences || [],
          video_id: videoId
        })
      }

      const taskId = response?.data?.task_id
      if (!taskId) throw new Error('No task ID')

      const resultUrl = await pollTask(taskId)

      setConversation(prev =>
        prev
          .filter(msg => msg.id !== loadingId)
          .concat({
            type: 'assistant',
            content: resultUrl,
            timestamp: Date.now()
          })
      )
      setSelectedForSave(resultUrl)
      setCurrentEditingImage(resultUrl)
      setSelectedReferences([])
    } catch (error) {
      setConversation(prev =>
        prev
          .filter(msg => msg.id !== loadingId)
          .concat({
            type: 'error',
            content: error.message || 'Generation failed',
            timestamp: Date.now()
          })
      )
    } finally {
      setIsGenerating(false)
    }
  }

  const handleImageSelect = url => {
    setSelectedForSave(url)
    setCurrentEditingImage(url)
  }

  const handleSave = () => {
    if (selectedForSave) {
      setShowConfirmSave(true)
    }
  }

  const confirmSave = () => {
    if (selectedForSave) {
      onConfirm(selectedForSave)
      setShowConfirmSave(false)
    }
  }

  const handleReferenceClick = url => {
    if (isGenerating) return
    const current = Array.isArray(selectedReferences) ? selectedReferences : []
    setSelectedReferences(
      current.includes(url) ? current.filter(u => u !== url) : [...current, url]
    )
  }

  const isReferenceSelected = url => {
    // Hide the selection border/checkmark if it's the current image being edited
    if (url === currentEditingImage) return false
    return Array.isArray(selectedReferences) && selectedReferences.includes(url)
  }

  const validReferenceImages = referenceImages.filter(
    img => img.url && img.url !== currentEditingImage
  )
  const selectedCount = (selectedReferences || []).length

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Get all generated images from conversation
  const generatedImages = conversation
    .filter(msg => msg.type === 'assistant')
    .map(msg => ({ url: msg.content, timestamp: msg.timestamp }))

  return (
    <Modal
      visible={visible}
      title={null}
      centered
      onCancel={onCancel}
      width={1000}
      maskClosable={false}
      bodyStyle={{
        padding: 0,
        height: '75vh',
        display: 'flex',
        flexDirection: 'column'
      }}
      footer={null}
    >
      {/* Minimal Header */}
      <div
        style={{
          padding: '12px 20px',
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: COLORS.white
        }}
      >
        <div style={{ fontWeight: 500, fontSize: 15, color: COLORS.text }}>
          Image Editor
          <span
            style={{
              marginLeft: 8,
              fontSize: 12,
              color: COLORS.textMuted,
              fontWeight: 400
            }}
          >
            ({mode === 'kontext' ? 'Single Reference' : 'Multi-Reference'})
          </span>
          <hr />
          <p style={{ fontSize: 12, color: COLORS.textMuted }}>
            Chat with AI to edit your image
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Chat Area */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            background: COLORS.white,
            margin: '24px 32px'
          }}
        >
          {/* Messages Area */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px 32px',
              background: '#f9f9f9ff',
              borderRadius: '20px'
            }}
          >
            {/* Empty State */}
            {conversation.length === 0 && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  color: COLORS.textMuted,
                  height: '100%',
                  justifyContent: 'center',
                  padding: '24px 32px',
                  borderRadius: '20px'
                }}
              >
                <div
                  style={{
                    height: 48,
                    width: 48,
                    background: '#5057d5',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 16
                  }}
                >
                  <Icon type="file-image" style={{ color: '#fff' }} size={24} />
                </div>

                <div style={{ fontSize: 16, marginBottom: 16 }}>
                  {mode === 'kontext'
                    ? 'Describe your edit'
                    : 'Select references and describe what to generate'}
                </div>
                {currentEditingImage && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 8,
                      padding: 12,
                      background: COLORS.white,
                      borderRadius: 12,
                      border: `1px solid ${COLORS.borderLight}`
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: COLORS.textMuted
                      }}
                    >
                      Image being edited
                    </div>
                    <ImagePreview
                      src={currentEditingImage}
                      alt="Current"
                      style={{
                        width: 120,
                        height: 120,
                        objectFit: 'cover',
                        borderRadius: 8
                      }}
                    />
                    {initialPrompt && (
                      <div
                        style={{
                          fontSize: 12,
                          color: COLORS.textSecondary,
                          fontStyle: 'italic',
                          textAlign: 'center',
                          marginTop: 4,
                          maxWidth: 300
                        }}
                      >
                        "{initialPrompt}"
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Messages */}
            {conversation.map((msg, idx) => (
              <div key={idx} style={{ marginBottom: 24 }}>
                {/* User Message */}
                {msg.type === 'user' && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <div
                      style={{
                        maxWidth: '65%',
                        background: COLORS.primaryLight,
                        padding: '14px 18px',
                        borderRadius: '20px 20px 4px 20px',
                        color: COLORS.text
                      }}
                    >
                      <div style={{ fontSize: 14, lineHeight: 1.6 }}>
                        {msg.content}
                      </div>
                      {msg.references?.filter(r => r).length > 0 && (
                        <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
                          {msg.references
                            .filter(r => r)
                            .map((ref, i) => (
                              <ImagePreview
                                key={i}
                                src={ref}
                                alt=""
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 4,
                                  objectFit: 'cover'
                                }}
                              />
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Loading */}
                {msg.type === 'loading' && (
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                  >
                    <div
                      style={{
                        background: '#fff',
                        padding: '14px 20px',
                        borderRadius: '4px 20px 20px 20px',
                        border: `1px solid ${COLORS.borderLight}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10
                      }}
                    >
                      <Spin size="small" />
                      <span
                        style={{ color: COLORS.textSecondary, fontSize: 14 }}
                      >
                        Generating...
                      </span>
                    </div>
                  </div>
                )}

                {/* Assistant Response */}
                {msg.type === 'assistant' && (
                  <div style={{ display: 'flex' }}>
                    <div
                      style={{
                        background: COLORS.white,
                        padding: 12,
                        borderRadius: '4px 20px 20px 20px',
                        border: `1px solid ${COLORS.border}`
                      }}
                    >
                      <ImageWithPlaceholder
                        src={msg.content}
                        alt="Generated"
                        aspectRatio={aspectRatio}
                        style={{
                          width: 260,
                          maxWidth: '100%',
                          borderRadius: 8,
                          display: 'block'
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Error */}
                {msg.type === 'error' && (
                  <div
                    style={{
                      background: '#fef2f2',
                      padding: '12px 16px',
                      borderRadius: '4px 20px 20px 20px',
                      border: '1px solid #fecaca',
                      color: '#dc2626',
                      fontSize: 14,
                      display: 'inline-block'
                    }}
                  >
                    {msg.content}
                  </div>
                )}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          {/* Input Area */}
          <div
            style={{
              padding: '16px 0',
              background: COLORS.white
            }}
          >
            {/* Advanced Settings */}
            {mode === 'kontext' && (
              <div style={{ marginBottom: 12 }}>
                <div
                  style={{
                    fontSize: 12,
                    color: COLORS.textMuted,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                  }}
                  onClick={() => setShowAdvanced(!showAdvanced)}
                >
                  <Icon
                    type={showAdvanced ? 'down' : 'right'}
                    style={{ fontSize: 10 }}
                  />
                  Advanced
                </div>
                {showAdvanced && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 16,
                      marginTop: 10,
                      padding: 12,
                      background: COLORS.background,
                      borderRadius: 6
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          color: COLORS.textMuted,
                          marginBottom: 4,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        Steps: {inferenceSteps}
                        <Tooltip title="Number of denoising steps. Higher values produce more detailed results but take longer.">
                          <Icon
                            type="info-circle"
                            style={{ fontSize: 11, cursor: 'help' }}
                          />
                        </Tooltip>
                      </div>
                      <Slider
                        min={10}
                        max={50}
                        value={inferenceSteps}
                        onChange={setInferenceSteps}
                        disabled={isGenerating}
                      />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          color: COLORS.textMuted,
                          marginBottom: 4,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        Guidance: {guidanceScale.toFixed(2)}
                        <Tooltip title="Controls how closely the output follows the prompt. Higher values = stronger adherence.">
                          <Icon
                            type="info-circle"
                            style={{ fontSize: 11, cursor: 'help' }}
                          />
                        </Tooltip>
                      </div>
                      <Slider
                        min={0.1}
                        max={2.0}
                        step={0.05}
                        value={guidanceScale}
                        onChange={setGuidanceScale}
                        disabled={isGenerating}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
              <TextArea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Describe your edit..."
                autoSize={{ minRows: 1, maxRows: 4 }}
                disabled={isGenerating}
                style={{
                  flex: 1,
                  borderRadius: 8,
                  padding: '10px 14px',
                  resize: 'none',
                  border: `1px solid ${COLORS.border}`
                }}
              />
              <Button
                type="primary"
                onClick={handleSend}
                loading={isGenerating}
                style={{
                  height: 40,
                  padding: '0 20px',
                  background: COLORS.primary,
                  borderColor: COLORS.primary
                }}
              >
                Send
              </Button>
              {generatedImages.length > 0 && (
                <Button
                  icon="history"
                  onClick={() => setShowHistory(true)}
                  style={{ height: 40 }}
                >
                  History ({generatedImages.length})
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Reference Panel */}
        <div
          style={{
            width: showReferences ? 220 : 44,
            borderLeft: `1px solid ${COLORS.borderLight}`,
            background: COLORS.white,
            transition: 'width 0.2s',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div
            style={{
              padding: 12,
              borderBottom: `1px solid ${COLORS.borderLight}`,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: showReferences ? 'space-between' : 'center'
            }}
            onClick={() => setShowReferences(!showReferences)}
          >
            {showReferences ? (
              <>
                <span
                  style={{ fontSize: 13, fontWeight: 500, color: COLORS.text }}
                >
                  References
                  {selectedCount > 0 && (
                    <Badge
                      count={selectedCount}
                      style={{ marginLeft: 8, backgroundColor: COLORS.primary }}
                    />
                  )}
                </span>
                <Icon
                  type="right"
                  style={{ color: COLORS.textMuted, fontSize: 12 }}
                />
              </>
            ) : (
              <Badge count={selectedCount} offset={[-4, 0]}>
                <Icon
                  type="picture"
                  style={{ fontSize: 16, color: COLORS.textMuted }}
                />
              </Badge>
            )}
          </div>

          {showReferences && (
            <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
              {validReferenceImages.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: 20,
                    color: COLORS.textMuted,
                    fontSize: 12
                  }}
                >
                  No references
                </div>
              ) : (
                <>
                  {/* Group images by type */}
                  {(() => {
                    const characterImages = validReferenceImages.filter(
                      img => img.sceneTitle === 'Character'
                    )
                    const sceneImages = validReferenceImages.filter(
                      img =>
                        img.sceneTitle !== 'Location' &&
                        img.sceneTitle !== 'Character' &&
                        img.sceneTitle !== 'Prop'
                    )
                    const locationImages = validReferenceImages.filter(
                      img => img.sceneTitle === 'Location'
                    )
                    const propImages = validReferenceImages.filter(
                      img => img.sceneTitle === 'Prop'
                    )

                    const renderImageGrid = (images, groupTitle) =>
                      images.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 500,
                              color: COLORS.textSecondary,
                              marginBottom: 8,
                              padding: '0 2px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4
                            }}
                          >
                            <Icon
                              type={
                                groupTitle === 'Locations'
                                  ? 'environment'
                                  : groupTitle === 'Characters'
                                  ? 'user'
                                  : groupTitle === 'Props'
                                  ? 'appstore'
                                  : 'picture'
                              }
                              style={{ fontSize: 12 }}
                            />
                            {groupTitle} ({images.length})
                          </div>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: 6
                            }}
                          >
                            {images.map((image, idx) => {
                              const selected = isReferenceSelected(image.url)
                              return (
                                <div
                                  key={image.url + idx}
                                  style={{
                                    position: 'relative',
                                    cursor: isGenerating
                                      ? 'not-allowed'
                                      : 'pointer',
                                    opacity: isGenerating ? 0.5 : 1,
                                    borderRadius: 6,
                                    overflow: 'hidden',
                                    border: `2px solid ${
                                      selected ? COLORS.primary : 'transparent'
                                    }`,
                                    transition: 'border-color 0.2s'
                                  }}
                                  onClick={() =>
                                    handleReferenceClick(image.url)
                                  }
                                >
                                  <img
                                    src={image.url}
                                    alt=""
                                    style={{
                                      width: '100%',
                                      aspectRatio: '1/1',
                                      objectFit: 'cover',
                                      display: 'block'
                                    }}
                                  />
                                  {/* Title overlay */}
                                  <div
                                    style={{
                                      position: 'absolute',
                                      bottom: 0,
                                      left: 0,
                                      right: 0,
                                      padding: '12px 4px 4px',
                                      background:
                                        'linear-gradient(transparent, rgba(0,0,0,0.6))',
                                      color: '#fff',
                                      fontSize: 9,
                                      textAlign: 'center',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }}
                                  >
                                    {image.shotTitle || 'Image'}
                                  </div>
                                  {selected && (
                                    <div
                                      style={{
                                        position: 'absolute',
                                        top: 3,
                                        right: 3,
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        background: COLORS.primary,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <Icon
                                        type="check"
                                        style={{ color: '#fff', fontSize: 9 }}
                                      />
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )

                    return (
                      <>
                        {renderImageGrid(sceneImages, 'Shots')}
                        {renderImageGrid(locationImages, 'Locations')}
                        {renderImageGrid(propImages, 'Props')}
                        {renderImageGrid(characterImages, 'Characters')}
                      </>
                    )
                  })()}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '14px 24px',
          borderTop: `1px solid ${COLORS.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: COLORS.white
        }}
      >
        <span style={{ fontSize: 13, color: COLORS.textSecondary }}>
          {selectedForSave ? (
            <>
              <Icon
                type="check-circle"
                theme="filled"
                style={{ color: COLORS.success, marginRight: 6 }}
              />
              Image ready to save
            </>
          ) : (
            'Select an image from History to save'
          )}
        </span>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button
            onClick={onCancel}
            disabled={isGenerating}
            style={{ height: 36 }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSave}
            disabled={!selectedForSave || isGenerating}
            style={{
              height: 36,
              background: COLORS.primary,
              borderColor: COLORS.primary,
              fontWeight: 500
            }}
          >
            Save Image
          </Button>
        </div>
      </div>

      {/* History Modal */}
      <Modal
        title="Select Generated Image"
        visible={showHistory}
        onCancel={() => setShowHistory(false)}
        footer={null}
        width={600}
        centered
      >
        {generatedImages.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: 40,
              color: COLORS.textMuted
            }}
          >
            No images generated yet
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 12,
              maxHeight: '60vh',
              overflowY: 'auto'
            }}
          >
            {generatedImages.map((img, idx) => (
              <div
                key={idx}
                style={{
                  position: 'relative',
                  cursor: 'pointer',
                  borderRadius: 8,
                  overflow: 'hidden',
                  border:
                    selectedForSave === img.url
                      ? `2px solid ${COLORS.primary}`
                      : '2px solid transparent'
                }}
                onClick={() => {
                  handleImageSelect(img.url)
                  setShowHistory(false)
                }}
              >
                <img
                  src={img.url}
                  alt="Generated"
                  style={{
                    width: '100%',
                    aspectRatio: '1/1',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />
                {selectedForSave === img.url && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      background: COLORS.primary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Icon
                      type="check"
                      style={{ color: '#fff', fontSize: 12 }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Save Confirmation Modal */}
      <Modal
        title="Confirm Image Replacement"
        visible={showConfirmSave}
        onCancel={() => setShowConfirmSave(false)}
        centered
        width={520}
        footer={[
          <Button key="cancel" onClick={() => setShowConfirmSave(false)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={confirmSave}
            style={{ background: COLORS.primary, borderColor: COLORS.primary }}
          >
            Confirm & Save
          </Button>
        ]}
      >
        <div style={{ textAlign: 'center' }}>
          <p
            style={{
              fontSize: 14,
              color: COLORS.textSecondary,
              marginBottom: 20
            }}
          >
            This action will replace the current shot image with your selected
            image.
          </p>
          <div
            style={{
              display: 'flex',
              gap: 20,
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            {/* Current Image */}
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: 12,
                  color: COLORS.textMuted,
                  marginBottom: 8,
                  fontWeight: 500
                }}
              >
                Current Image
              </div>
              <div
                style={{
                  width: 160,
                  height: 160,
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: `1px solid ${COLORS.border}`,
                  background: COLORS.background
                }}
              >
                {currentImage ? (
                  <img
                    src={currentImage}
                    alt="Current"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: COLORS.textMuted,
                      fontSize: 12
                    }}
                  >
                    No image
                  </div>
                )}
              </div>
            </div>

            {/* Arrow */}
            <div style={{ color: COLORS.textMuted }}>
              <Icon type="arrow-right" style={{ fontSize: 24 }} />
            </div>

            {/* New Image */}
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: 12,
                  color: COLORS.primary,
                  marginBottom: 8,
                  fontWeight: 500
                }}
              >
                New Image
              </div>
              <div
                style={{
                  width: 160,
                  height: 160,
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: `2px solid ${COLORS.primary}`,
                  boxShadow: '0 2px 8px rgba(26, 115, 232, 0.2)'
                }}
              >
                {selectedForSave && (
                  <img
                    src={selectedForSave}
                    alt="New"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </Modal>
  )
}

export default ImageEditorModal
